import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ConfirmProvider } from "@/context/ConfirmContext";
import { PdfViewerProvider } from "@/context/PdfViewerContext";
import { ActivityProvider, useActivity } from "@/context/ActivityContext";
import { OfflineProvider } from "@/context/OfflineContext";
import OfflinePinGate from "@/components/OfflinePinGate";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import ForcePassword from "@/pages/ForcePassword";
import Dashboard from "@/pages/Dashboard";
import POS from "@/pages/POS";
import Receivables from "@/pages/Receivables";
import Quotes from "@/pages/Quotes";
import Stock from "@/pages/Stock";
import Products from "@/pages/Products";
import PrintBarcodes from "@/pages/PrintBarcodes";
import Guide from "@/pages/Guide";
import Reports from "@/pages/Reports";
import Returns from "@/pages/Returns";
import Categories from "@/pages/Categories";
import Parties from "@/pages/Parties";
import Purchases from "@/pages/Purchases";
import CashRegister from "@/pages/CashRegister";
import Expenses from "@/pages/Expenses";
import HR from "@/pages/HR";
import Fleet from "@/pages/Fleet";
import Rentals from "@/pages/Rentals";
import Deliveries from "@/pages/Deliveries";
import Activities from "@/pages/Activities";
import UsersPage from "@/pages/UsersPage";
import Audit from "@/pages/Audit";
import SettingsPage from "@/pages/SettingsPage";
import PlatformAdmin from "@/pages/PlatformAdmin";
import Locations from "@/pages/Locations";
import Supplies from "@/pages/Supplies";
import SellerReport from "@/pages/SellerReport";
import Cancellations from "@/pages/Cancellations";
import Authorizations from "@/pages/Authorizations";
import Formation from "@/pages/Formation";
import Account from "@/pages/Account";
import PageTitle from "@/components/PageTitle";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#64748B]">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePassword />;
  return children;
}

function RequireRole({ roles, mod, children }) {
  const { user } = useAuth();
  const { modules } = useActivity();
  const roleOk = user.role === "owner" || roles.includes(user.role);
  const modOk = mod && Array.isArray(modules) && modules.includes(mod);
  if (!roleOk && !modOk) return <Navigate to="/" replace />;
  return children;
}

function RequireSellers({ children }) {
  const { current, multiSeller, loading } = useActivity();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#64748B]">Chargement…</div>;
  if (!current || current.global) return <Navigate to="/" replace />;
  if (multiSeller === null) return <div className="min-h-screen flex items-center justify-center text-[#64748B]">Chargement…</div>;
  if (multiSeller === false) return <Navigate to="/" replace />;
  return children;
}

function ModuleRoute({ mod, children }) {
  const { modules } = useActivity();
  if (modules && !modules.includes(mod)) return <Navigate to="/" replace />;
  return children;
}

const SELLER_ROLES = ["cashier", "seller"];
function isSellerProfile(user) {
  return !!user && !user.is_owner && SELLER_ROLES.includes(user.role);
}

function HomeRedirect() {
  const { user } = useAuth();
  // Profil vendeur/caissier : la caisse est son écran d'accueil (pas de tableau de bord).
  if (isSellerProfile(user)) return <Navigate to="/pos" replace />;
  return <Dashboard />;
}

function RequireSell({ children }) {
  const { user } = useAuth();
  const { access, canSell } = useActivity();
  if (access == null) return <div className="min-h-screen flex items-center justify-center text-[#64748B]">Chargement…</div>;
  if (canSell === false) {
    // Évite une boucle de redirection pour un vendeur dont l'accueil est le POS.
    if (isSellerProfile(user)) return <div className="min-h-[60vh] flex items-center justify-center text-center text-[#64748B] p-6">Aucun point de vente ne vous est attribué. Contactez votre administrateur.</div>;
    return <Navigate to="/" replace />;
  }
  return children;
}

function RequireSupplies({ children }) {
  // Approvisionnements : caissiers/vendeurs n'y accèdent qu'en mode entrepôt + PDV (réception). En mono-boutique → caisse.
  const { user } = useAuth();
  const { current, loading } = useActivity();
  if (loading || !current) return <div className="min-h-screen flex items-center justify-center text-[#64748B]">Chargement…</div>;
  if (isSellerProfile(user) && (current.global || current.stock_mode !== "advanced")) return <Navigate to="/pos" replace />;
  return children;
}

function RequireLocations({ children }) {
  const { access, locationsVisible } = useActivity();
  if (access == null) return <div className="min-h-screen flex items-center justify-center text-[#64748B]">Chargement…</div>;
  if (locationsVisible !== true) return <Navigate to="/" replace />;
  return children;
}

function Shell() {
  const { user } = useAuth();
  if (user && user.role === "superadmin") return <Navigate to="/admin" replace />;
  return (
    <ActivityProvider>
      <OfflineProvider>
        <Layout />
      </OfflineProvider>
    </ActivityProvider>
  );
}

function RequireSuperadmin({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-[#64748B]">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "superadmin") return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ConfirmProvider>
        <PdfViewerProvider>
        <PageTitle />
        <Toaster position="bottom-right" richColors />
        <OfflinePinGate />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/admin" element={<RequireSuperadmin><PlatformAdmin /></RequireSuperadmin>} />
          <Route path="/print/barcodes" element={<Protected><PrintBarcodes /></Protected>} />
          <Route path="/" element={<Protected><Shell /></Protected>}>
            <Route index element={<HomeRedirect />} />
            <Route path="guide" element={<Guide />} />
            <Route path="formation" element={<Formation />} />
            <Route path="account" element={<Account />} />
            <Route path="reports" element={<RequireRole roles={["admin", "manager", "accountant"]}><Reports /></RequireRole>} />
            <Route path="seller-report" element={<RequireRole roles={["admin", "manager", "accountant"]}><RequireSellers><SellerReport /></RequireSellers></RequireRole>} />
            <Route path="cancellations" element={<RequireRole roles={["admin", "manager", "accountant"]}><Cancellations /></RequireRole>} />
            <Route path="authorizations" element={<RequireRole roles={["admin", "manager"]}><Authorizations /></RequireRole>} />
            <Route path="locations" element={<RequireRole roles={["admin", "manager"]}><RequireLocations><Locations /></RequireLocations></RequireRole>} />
            <Route path="supplies" element={<RequireRole roles={["admin", "manager", "storekeeper", "cashier", "seller"]}><RequireSupplies><Supplies /></RequireSupplies></RequireRole>} />
            <Route path="returns" element={<ModuleRoute mod="returns"><Returns /></ModuleRoute>} />
            <Route path="pos" element={<ModuleRoute mod="pos"><RequireSell><POS /></RequireSell></ModuleRoute>} />
            <Route path="receivables" element={<ModuleRoute mod="receivables"><Receivables /></ModuleRoute>} />
            <Route path="quotes" element={<RequireRole roles={["admin", "manager", "cashier"]} mod="quotes"><ModuleRoute mod="quotes"><Quotes /></ModuleRoute></RequireRole>} />
            <Route path="stock" element={<ModuleRoute mod="stock"><Stock /></ModuleRoute>} />
            <Route path="products" element={<ModuleRoute mod="products"><Products /></ModuleRoute>} />
            <Route path="categories" element={<ModuleRoute mod="products"><Categories /></ModuleRoute>} />
            <Route path="parties" element={<ModuleRoute mod="parties"><Parties /></ModuleRoute>} />
            <Route path="purchases" element={<RequireRole roles={["admin", "manager", "storekeeper", "accountant"]} mod="purchases"><ModuleRoute mod="purchases"><Purchases /></ModuleRoute></RequireRole>} />
            <Route path="cash" element={<ModuleRoute mod="cash"><CashRegister /></ModuleRoute>} />
            <Route path="expenses" element={<ModuleRoute mod="expenses"><Expenses /></ModuleRoute>} />
            <Route path="hr" element={<RequireRole roles={["admin", "accountant"]} mod="hr"><ModuleRoute mod="hr"><HR /></ModuleRoute></RequireRole>} />
            <Route path="fleet" element={<RequireRole roles={["admin", "manager", "storekeeper"]} mod="fleet"><ModuleRoute mod="fleet"><Fleet /></ModuleRoute></RequireRole>} />
            <Route path="rentals" element={<RequireRole roles={["admin", "manager", "cashier"]} mod="rentals"><ModuleRoute mod="rentals"><Rentals /></ModuleRoute></RequireRole>} />
            <Route path="deliveries" element={<RequireRole roles={["admin", "manager", "storekeeper", "cashier"]} mod="deliveries"><ModuleRoute mod="deliveries"><Deliveries /></ModuleRoute></RequireRole>} />
            <Route path="activities" element={<RequireRole roles={["admin", "manager"]}><Activities /></RequireRole>} />
            <Route path="users" element={<RequireRole roles={["admin"]}><UsersPage /></RequireRole>} />
            <Route path="audit" element={<RequireRole roles={["admin", "auditor", "accountant"]}><Audit /></RequireRole>} />
            <Route path="settings" element={<RequireRole roles={["admin"]}><SettingsPage /></RequireRole>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </PdfViewerProvider>
        </ConfirmProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
